import React from 'react';
import { formatDate } from 'utils';
import Card from 'components/Card/Card';
import { useAsync } from 'react-async-hook';
import { providerService } from 'services';
import { Link, useParams } from 'react-router-dom/cjs/react-router-dom';
import { Spin, Typography } from 'antd';
import Table from 'components/Table';
import { ENDASH } from 'constants';
import EmptyState from 'components/EmptyState/EmptyState';
import PageMeta from 'components/PageMeta/PageMeta';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import FormattedContact from 'components/FormattedContact/FormattedContact';
import FormattedAddress from 'components/FormattedAddress/FormattedAddress';

export default function ProviderLocationDetails() {
  const { orgId, facilityId } = useParams();

  // Get organization details
  const { loading: organizationLoading, result: organization = null } = useAsync(async () => {
    if (orgId) {
      return providerService.getOrganization(orgId);
    }
    return null;
  }, [orgId]);

  // Get care facility details
  const { loading: facilityLoading, result: facility = null } = useAsync(async () => {
    if (facilityId) {
      return providerService.getCenterById(facilityId);
    }
    return null;
  }, [facilityId]);

  // Get applications for this care facility
  const { loading: applicationsLoading, result: applications = [] } = useAsync(async () => {
    if (facilityId) {
      return providerService.getCarefacilityApplications(facilityId);
    }
    return [];
  }, [facilityId]);

  if (facility === null || organization === null) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  const admin = organization?.admin || {};

  return (
    <>
      <PageMeta title="Provider location details" />

      {facility && (
        <Card title="Provider location details" className="max-w-[600px] mb-12">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex flex-col gap-2">
              <Typography.Text>
                <span className="!font-semibold">Org ID:</span> {orgId ?? ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Name:</span>{' '}
                {admin?.firstName && admin?.lastName ? `${admin.firstName} ${admin.lastName}` : ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Business Name:</span> {organization?.businessLegalName ?? ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Location ID:</span> {facilityId ?? ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Create Date:</span>{' '}
                {facility?.createdDate ? formatDate(facility.createdDate) : ENDASH}
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Doing Business As Name:</span> {facility?.businessLegalName ?? ENDASH}
              </Typography.Text>
            </div>

            <div className="flex flex-col gap-2">
              <Typography.Text>
                <span className="!font-semibold">Location Address:</span>{' '}
                <FormattedAddress address={facility?.address} />
              </Typography.Text>

              <Typography.Text>
                <span className="!font-semibold">Location Contact:</span>{' '}
                <FormattedContact contact={facility?.facilityContact} />
              </Typography.Text>
            </div>
          </div>
        </Card>
      )}

      {applications && applications.length > 0 ? (
        <Card
          noBodyPadding
          title={`${applications.length} provider application${applications.length !== 1 ? 's' : ''}`}
        >
          <Table
            dataSource={applications}
            columns={[
              {
                title: 'Application ID',
                dataIndex: 'id',
                render: (id) => (
                  <Link
                    onClick={(e) => e.stopPropagation()}
                    className="underline py-2"
                    to={`/providers/applications/${id}`}
                  >
                    {id}
                  </Link>
                ),
              },
              {
                title: 'Application Type',
                dataIndex: 'programType',
                render: (programType) => programType?.title ?? ENDASH,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                render: (status) => status?.status?.description ?? ENDASH,
              },
              {
                title: 'Assignee',
                dataIndex: 'assignedTo',
                render: (assignedTo) => {
                  if (!assignedTo) return ENDASH;
                  return assignedTo?.fullName ?? ENDASH;
                },
              },
              {
                title: 'Created Date',
                dataIndex: 'createdDate',
                render: (createdDate, record) => {
                  const _date = createdDate || record?.submittedDate || record?.lastModifiedDate;
                  return _date ? formatDate(_date, DATE_FORMAT_SENTENCE) : ENDASH;
                },
              },
              {
                title: 'Submitted Date',
                dataIndex: 'submittedDate',
                render: (date) => {
                  return date ? formatDate(date, DATE_FORMAT_SENTENCE) : ENDASH;
                },
              },
              {
                title: 'Time in Status',
                dataIndex: 'daysInStatus',
                render: (days) => {
                  if (!days) return ENDASH;
                  return `${days} day${days !== 1 ? 's' : ''}`;
                },
              },
            ]}
            childrenColumnName="_"
            loading={applicationsLoading || facilityLoading || organizationLoading}
          />
        </Card>
      ) : (
        <Card>
          <EmptyState description="No applications found for this provider location" />
        </Card>
      )}
    </>
  );
}
